import { useCallback, useState } from 'react';
import {
  extensionInstallSchema,
  type ExtensionId,
  type ExtensionInstall,
} from '@forecastify/shared';
import type { Toast } from '@forecastify/ui';
import { useT } from '../../i18n';
import { EXTENSION_TOPICS } from '../../live/topics';
import { useLiveInvalidation } from '../../live/use-live';
import { useSession } from '../../session';
import { parseList } from '../permissions-hook';
import { failureOf, type ScreenFailure } from '../screen-state';
import { useFirstLoad } from '../use-first-load';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ What this workspace has switched on - items 68 and 67.
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️⚠️ **THIS WAS THE MARKETPLACE'S HOOK, AND THE MARKETPLACE IS GONE - item 68 / D7.** The
 * install step is a per-workspace TOGGLE on the integrations page now: same endpoint, same
 * `workspace_extensions` row, same live topic. What was deleted is a full-screen catalogue with
 * its own two addresses, a settings-nav entry and a card layout - for a decision with two possible
 * values, on two extensions, that belonged beside the thing it enables.
 *
 * ⚠️ **THE READ IS THE INSTALLS, NOT THE CATALOGUE.** `GET /extensions` returns which of the
 * entries this workspace has; the entries themselves are a constant in `shared` that the browser
 * already holds. An endpoint returning them would ship the same list twice and translate none of
 * it.
 *
 * ⚠️ **`live`, on the `extension` topic.** Switching one on is a workspace-wide act somebody
 * else can perform - an admin in another tab turns Jira on, and this list has to notice. R-19.
 */

let toastCounter = 0;
const nextToastId = (): string => `extensions-${String((toastCounter += 1))}`;

export function useInstalls(): {
  installs: readonly ExtensionInstall[];
  /** Until the first answer: no switch claims to be off. */
  loading: boolean;
  failure: ScreenFailure | null;
  busy: boolean;
  toasts: readonly Toast[];
  dismiss: (id: string | number) => void;
  reload: () => Promise<void>;
  install: (id: ExtensionId) => void;
  uninstall: (id: ExtensionId) => void;
} {
  const session = useSession();
  const t = useT();
  const [installs, setInstalls] = useState<readonly ExtensionInstall[]>([]);
  const [failure, setFailure] = useState<ScreenFailure | null>(null);
  const [busy, setBusy] = useState(false);
  const [toasts, setToasts] = useState<readonly Toast[]>([]);

  const reload = useCallback(async (): Promise<void> => {
    if (session.workspaceId === null) {
      setFailure({ kind: 'no-workspace' });
      return;
    }
    const response = await session.request('/extensions').catch(() => null);
    if (response === null) {
      setFailure({ kind: 'error', code: 'unavailable' });
      return;
    }
    if (!response.ok) {
      setFailure(await failureOf(response));
      return;
    }
    setInstalls(parseList(extensionInstallSchema, await response.json()) ?? []);
    setFailure(null);
    /* eslint-disable-next-line react-hooks/exhaustive-deps -- `session` is a fresh object every
       render (C20's ten-requests bug, `preferences-boot.tsx`); depending on it refetches for ever. */
  }, [session.workspaceId]);

  /* First load draws skeleton rows — `use-first-load.ts`. */
  const loading = useFirstLoad(reload);

  useLiveInvalidation(EXTENSION_TOPICS, () => {
    void reload();
  });

  const write = (id: ExtensionId, init: RequestInit, path: string, done: () => Toast): void => {
    void (async () => {
      setBusy(true);
      const response = await session.request(path, init).catch(() => null);
      setBusy(false);
      if (response?.ok !== true) {
        const body = (await response?.json().catch(() => null)) as {
          error?: { code?: string; details?: { instances?: unknown } };
        } | null;

        /**
         * ⚠️⚠️ **`extension_in_use` NAMES WHAT IS IN THE WAY - item 68 / D7.** A refusal that says
         * *"delete your instances first"* without saying WHICH sends somebody hunting through a
         * screen they have just been refused entry to. The names travel in `details` for exactly
         * this sentence.
         *
         * ⚠️ **Read defensively, because `details` is not a schema.** It is an error envelope
         * from a server that may be a version ahead; a non-array here falls through to the generic
         * message rather than rendering `[object Object]` at somebody.
         */
        const named = body?.error?.details?.instances;
        if (body?.error?.code === 'extension_in_use' && Array.isArray(named)) {
          setToasts((current) => [
            ...current,
            {
              id: nextToastId(),
              title: t('integrations.inUse', { names: named.map(String).join(', ') }),
              variant: 'error',
            },
          ]);
          return;
        }

        /* ⚠️ Branching on the CODE - rule 5. `not_found` is an id the catalogue does not carry,
           which means a stale tab or a hand-made request. */
        setToasts((current) => [
          ...current,
          {
            id: nextToastId(),
            title: t(
              body?.error?.code === 'not_found' ? 'integrations.notFound' : 'error.unavailable',
            ),
            variant: 'error',
          },
        ]);
        return;
      }
      /* ⚠️ The response IS the new list, so this does not refetch — the service returns it for
         exactly this reason, and a second GET would race the first. */
      setInstalls(parseList(extensionInstallSchema, await response.json()) ?? []);
      setToasts((current) => [...current, done()]);
    })();
  };

  return {
    installs,
    loading,
    failure,
    busy,
    toasts,
    dismiss: (id: string | number) => {
      setToasts((current) => current.filter((entry) => entry.id !== id));
    },
    reload,
    install: (id) => {
      write(
        id,
        { method: 'POST', body: JSON.stringify({ extensionId: id }) },
        '/extensions',
        () => ({
          id: nextToastId(),
          title: t('integrations.toast.enabled'),
          description: t('integrations.toast.enabledBody'),
          variant: 'success',
        }),
      );
    },
    uninstall: (id) => {
      write(id, { method: 'DELETE' }, `/extensions/${id}`, () => ({
        id: nextToastId(),
        title: t('integrations.toast.disabled'),
        variant: 'info',
      }));
    },
  };
}
